import React, { FunctionComponent, useState } from 'react';
import {
  Card,
  CardActionArea,
  CardContent,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  Radio,
  Stack,
  SxProps,
  Typography,
} from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';

import {
  COURSE_PRICING_TYPES,
  ICoursePricing,
} from '../../../../store/V2/CoursesSlice';
import DarkLoadingButton, {
  defaultButtonStyles,
} from '../../../../components/V2/Buttons';

interface CourseSubscriptionOptionsModalProps {
  open: boolean;
  onClose: () => void;
  pricings: ICoursePricing[];
  onSubmit: (option: ICoursePricing) => void;
}

type TCardProps = {
  pricing: ICoursePricing;
  onClick: () => void;
  selected?: boolean;
};

const cardStyles: SxProps = {
  border: '1px solid #CBD5E1',
};

const CustomCard = ({ pricing, onClick, selected = false }: TCardProps) => {
  let amount = 0;
  let currency = '';
  let pricingType = pricing.type.toLowerCase();

  if (pricing.type === COURSE_PRICING_TYPES.CREDIT) {
    pricingType = 'From your credits';
  }

  if (
    pricing.type !== COURSE_PRICING_TYPES.CREDIT &&
    pricing.amount &&
    pricing.currency
  ) {
    amount = pricing.amount;
    currency = pricing.currency.toUpperCase();
  }

  return (
    <Card elevation={0} sx={cardStyles}>
      <CardActionArea onClick={onClick}>
        <CardContent style={{ padding: '8px' }}>
          <Stack direction="row" alignItems="center">
            <Radio checked={selected} />
            <Stack>
              <Typography
                fontSize="14px"
                fontWeight={500}
                sx={{ textTransform: 'capitalize' }}
                className="ellipsis-text one-line"
              >
                {pricingType}
              </Typography>
              {pricing.type !== COURSE_PRICING_TYPES.CREDIT &&
              pricing.amount &&
              pricing.currency ? (
                <Typography
                  fontSize="16px"
                  fontWeight={600}
                  className="ellipsis-text one-line"
                >
                  {currency} {amount.toFixed(2)}
                  {pricing.type === COURSE_PRICING_TYPES.SUBSCRIPTION &&
                  pricing.billingPeriod ? (
                    <span style={{ color: '#647488', fontWeight: 500 }}>
                      &nbsp;per&nbsp;
                      {pricing.billingPeriod.count > 1
                        ? `${pricing.billingPeriod.count} `
                        : null}
                      {pricing.billingPeriod.type.toLowerCase().slice(0, -1)}
                      {pricing.billingPeriod.count > 1 ? 's' : ''}
                    </span>
                  ) : null}
                </Typography>
              ) : null}
            </Stack>
          </Stack>
        </CardContent>
      </CardActionArea>
    </Card>
  );
};

const CourseSubscriptionOptionsModal: FunctionComponent<CourseSubscriptionOptionsModalProps> = ({
  open,
  onClose,
  pricings,
  onSubmit,
}) => {
  // State
  const [
    selectedPricingOption,
    setSelectedPricingOption,
  ] = useState<ICoursePricing | null>(null);

  const modalTitle = 'Course Enrolment Options';

  const onPricingOptionChange = (option: ICoursePricing) => {
    setSelectedPricingOption(option);
  };

  const handleSubmit = () => {
    if (selectedPricingOption) {
      onSubmit(selectedPricingOption);
      onClose();
    }
  };

  return (
    <Dialog
      fullWidth
      maxWidth="sm"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: { borderRadius: '20px' },
      }}
    >
      <DialogTitle
        sx={{
          m: 0,
          p: { xs: '15px', sm: '30px' },
          color: '#000000',
          fontWeight: 600,
          fontSize: '16px',
        }}
      >
        {modalTitle}
      </DialogTitle>
      <IconButton
        aria-label="close"
        onClick={onClose}
        sx={{
          position: 'absolute',
          right: { xs: '7px', sm: '22px' },
          top: { xs: '7px', sm: '22px' },
        }}
      >
        <CloseIcon sx={{ color: '#000000' }} />
      </IconButton>
      <DialogContent
        sx={{ p: { xs: '0 15px 15px 15px', sm: '0 30px 30px 30px' } }}
      >
        <div>
          <Grid container spacing={2}>
            {pricings.map((item, index) => {
              return (
                <Grid key={`subscribe-option-${index}`} item xs={12}>
                  <CustomCard
                    pricing={item}
                    selected={item === selectedPricingOption}
                    onClick={() => onPricingOptionChange(item)}
                  />
                </Grid>
              );
            })}
          </Grid>
        </div>
      </DialogContent>
      <DialogActions sx={{ p: { xs: '15px', sm: '30px' } }}>
        <DarkLoadingButton
          disableElevation
          variant="contained"
          color="inherit"
          onClick={handleSubmit}
          disabled={!selectedPricingOption}
          sx={{ ...defaultButtonStyles }}
        >
          Enroll
        </DarkLoadingButton>
      </DialogActions>
    </Dialog>
  );
};

export default CourseSubscriptionOptionsModal;
